// src/views/SellerDash.js — extracted from App.js
import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { upsertOne } from "../lib/supabase";
import { compressImage, timeAgo, liveOffers, isOfferLive, acceptedOffers } from "../lib/utils";
import { SK, SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from "../constants";
import { MakeOfferModal, WithdrawOfferModal, EditOfferPriceModal, OFFER_PLATFORM_FEE } from "../components/MakeOfferModal";
import { CounterOfferModal, CounterHistory, getCounterState } from "../components/CounterOfferModal";
import { RatingModal } from "../components/RatingModal";

// ─── SELLER DASHBOARD ────────────────────────────────────────────────────────
export function SellerDash({ session, sellers, saveSellers, listings, saveListings, requests, saveRequests, refreshRequests, catalogEntries, saveCatalog, toast, addNotif, reviews, saveReviews, users=[], setView, openDealRoom, initialTab="overview" }) {
  const mySellerId   = session?.user?.sellerId;
  const mySeller     = sellers.find(s=>s.id===mySellerId);
  const myListings   = listings.filter(l=>l.sellerId===mySellerId);
  const myListingIds = new Set(myListings.map(l=>l.id));
  const myEnquiries  = (requests||[]).filter(r=>r.type==="enquiry"&&myListingIds.has(r.listingId));
  const OPEN_STATUSES = new Set(["Received","Awaiting Photos","Searching"]);
  const isCollectorUser = (uid) => {
    const u = users.find(x => x.id === uid);
    return !!u && ((u.roles||[]).includes("collector") || u.role === "collector" || (u.roles||[]).includes("admin") || u.role === "admin");
  };
  // Request is open for new offers if Searching/Received/etc., OR (Match Found and the buyer is a collector — they can keep accepting more)
  const openSourcing = (requests||[]).filter(r =>
    (r.type==="catalog"||r.type==="sourcing") &&
    r.status !== "Expired" && r.status !== "Cancelled" && r.status !== "Completed" &&
    (OPEN_STATUSES.has(r.status) || (r.status === "Match Found" && isCollectorUser(r.userId))) &&
    !r.sellerClaim  // legacy single-claim hidden from new-offer flow
  );
  // Requests where I (this seller) have an accepted offer — these are my deals to dispatch
  const myAccepted   = (requests||[]).filter(r =>
    (r.offers||[]).some(o => o.sellerId === mySellerId && o.status === "accepted")
  );
  // Legacy: requests where I single-claimed (pre-multi-offer) — keep dispatch UI working for in-flight ones
  const myClaims     = (requests||[]).filter(r=>(r.type==="catalog"||r.type==="sourcing")&&r.sellerClaim?.sellerId===mySellerId);
  // Cross-request flat list of all my offers (any status) for visibility
  const myOffersFlat = (requests||[]).flatMap(r => (r.offers||[]).filter(o => o.sellerId === mySellerId).map(o => ({...o, request:r})));
  const [tab,setTab]           = useState(initialTab);
  const [lf,setLF]             = useState({brand:"",model:"",reference:"",decade:"",condition:"",price:"",description:""});
  const [listingPhotos,setLP]  = useState([]);
  const [listingUpl,setLUpl]   = useState(false);
  const [refreshing,setRefreshing] = useState(false);
  const [ratingTarget,setRatingTarget] = useState(null); // {requestId,toUserId,toName,fromRole}
  const lu=(k,v)=>setLF(p=>({...p,[k]:v}));

  // Refresh requests from DB when seller opens the dashboard
  useEffect(() => { if (refreshRequests) refreshRequests(); }, []);

  const doRefresh = async () => { setRefreshing(true); await refreshRequests?.(); setRefreshing(false); };

  const submitListing = async () => {
    if(!lf.brand||!lf.model||!lf.price){toast("Brand, model, price required.");return;}
    setLUpl(true);
    const lid = "l"+Date.now();
    let photoUrls=[];
    for(let i=0;i<listingPhotos.length;i++){
      try{
        const compressed=await compressImage(listingPhotos[i].file,1600,0.88);
        const path=`listings/${lid}/${i}.jpg`;
        const{error}=await supabase.storage.from("watch-photos").upload(path,compressed,{upsert:true,contentType:"image/jpeg"});
        if(!error){const{data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(path);photoUrls.push(publicUrl);}
      }catch{}
    }
    setLUpl(false);
    const newListing = {...lf,id:lid,sellerId:mySellerId,verified:false,photos:photoUrls,createdAt:new Date().toISOString(),price:parseInt(lf.price)};
    const catEntry = {
      id:"cat-"+(Date.now()+1), brand:lf.brand, model:lf.model,
      decade:lf.decade||"", condition:lf.condition||"",
      price:parseInt(lf.price), description:lf.description,
      photos:photoUrls,
      entryType:"for_sale", status: mySeller?.verified ? "approved" : "pending",
      collectorName:mySeller?.name||session.user.name,
      collectorId:mySellerId, listingId:lid,
      createdAt:new Date().toISOString(),
    };
    try { await upsertOne("listings", newListing); } catch(e) { toast("Failed to save listing: "+e.message); setLUpl(false); return; }
    try { await upsertOne("catalog_entries", catEntry); } catch(e) { toast("Failed to create catalog entry: "+e.message); }
    saveListings([...listings, newListing]);
    saveCatalog([...(catalogEntries||[]), catEntry]);
    setLF({brand:"",model:"",reference:"",decade:"",condition:"",price:"",description:""});
    setLP([]);
    toast(mySeller?.verified ? "Listing live on Explore!" : "Listing submitted for review."); setTab("listings");
  };

  // Offer modal state — { mode: "post"|"withdraw"|"edit", request, offer? }
  const [offerModal, setOfferModal] = useState(null);
  const [dispatchForm, setDispatchForm] = useState({});
  const [counterModal, setCounterModal] = useState(null); // {request, offer}

  const postOffer = async (req, newOffer) => {
    const updatedReq = {
      ...req,
      offers: [...(req.offers||[]), newOffer],
      updates: [...(req.updates||[]), {date:new Date().toISOString(), note:`${newOffer.sellerName} posted an offer at ₹${newOffer.price.toLocaleString("en-IN")} (${newOffer.condition}).`}],
      status: req.status === "Received" || req.status === "Awaiting Photos" ? "Searching" : req.status,
    };
    try { await upsertOne(SK.requests, updatedReq); } catch { toast("Failed to post offer — please retry."); return; }
    saveRequests(requests.map(r => r.id !== req.id ? r : updatedReq));
    // Auto-create deal room so seller and buyer can chat immediately
    const sellerUserId = session.user.id;
    const adminIds = (users||[]).filter(u=>(u.roles||[]).includes("admin")||u.role==="admin").map(u=>u.id);
    const participants = [...new Set([req.userId, sellerUserId, ...adminIds].filter(Boolean))];
    const { error: roomErr } = await supabase.from("chat_rooms").upsert({
      id: "deal-" + req.id + "-" + sellerUserId,
      type: "deal",
      name: req.watch + " — Deal Room",
      request_id: req.id,
      participants,
      created_at: new Date().toISOString(),
    }, { onConflict: "id", ignoreDuplicates: true });
    if (roomErr) console.error("deal room create failed:", roomErr);
    setOfferModal(null);
    toast("Offer posted. Buyer notified.");
    const dealRoomId = "deal-" + req.id + "-" + sellerUserId;
    if (addNotif) addNotif(req.userId, {
      type:"offer",
      title:`New offer on your ${req.watch}`,
      body:`${newOffer.sellerName} is offering ₹${newOffer.price.toLocaleString("en-IN")} (${newOffer.condition}). Tap to open the deal room.`,
      link:{view:"chat", roomId:dealRoomId},
      emailTo:req.email,
      emailSubject:`New offer on your ${req.watch}`,
      whatsappTo:req.phone,
      whatsappTemplate:"thc_buyer_update",
      whatsappParams:[{name:"1",value:req.name||"Collector"},{name:"2",value:req.watch},{name:"3",value:`A verified seller made an offer of ₹${newOffer.price.toLocaleString("en-IN")}. Open the THC app to review.`}],
    });
  };

  const handleSellerCounter = async (req, offer, newPrice, note) => {
    const counter = { price: newPrice, note, by: "seller", proposedAt: new Date().toISOString() };
    const updatedOffer = { ...offer, counterOffers: [...(offer.counterOffers||[]), counter] };
    const updatedReq = { ...req, offers: (req.offers||[]).map(o => o.id===offer.id ? updatedOffer : o) };
    try { await upsertOne(SK.requests, updatedReq); } catch { toast("Failed — please retry."); return; }
    saveRequests(requests.map(r => r.id===req.id ? updatedReq : r));
    setCounterModal(null);
    toast("Counter sent to buyer.");
    if (addNotif) addNotif(req.userId, {
      type:"counter",
      title:`Seller countered on ${req.watch}`,
      body:`New offer: ₹${newPrice.toLocaleString("en-IN")}${note?` — "${note}"`:""}`,
      link:{view:"request"},
      emailTo: req.email,
      emailSubject:`Counter offer on your ${req.watch}`,
      whatsappTo:req.phone,
      whatsappTemplate:"thc_buyer_update",
      whatsappParams:[{name:"1",value:req.name||"Collector"},{name:"2",value:req.watch},{name:"3",value:`Seller countered at ₹${newPrice.toLocaleString("en-IN")}${note?` — ${note}`:""}.`}],
    });
  };

  const handleAcceptCounter = async (req, offer) => {
    const cs = getCounterState(offer);
    const updatedOffer = { ...offer, status:"accepted", price: cs.currentPrice, acceptedAt: new Date().toISOString() };
    const updatedReq = { ...req, offers: (req.offers||[]).map(o => o.id===offer.id ? updatedOffer : o), status:"Match Found" };
    try { await upsertOne(SK.requests, updatedReq); } catch { toast("Failed — please retry."); return; }
    saveRequests(requests.map(r => r.id===req.id ? updatedReq : r));
    toast(`Counter accepted at ₹${cs.currentPrice.toLocaleString("en-IN")}.`);
    if (addNotif) addNotif(req.userId, { type:"offer-accepted", title:`Offer accepted — ${req.watch}`, body:`The seller accepted your counter at ₹${cs.currentPrice.toLocaleString("en-IN")}.`, link:{view:"request"}, emailTo:req.email, emailSubject:`Offer accepted — ${req.watch}`, whatsappTo:req.phone, whatsappTemplate:"thc_buyer_update", whatsappParams:[{name:"1",value:req.name||"Collector"},{name:"2",value:req.watch},{name:"3",value:`Your counter of ₹${cs.currentPrice.toLocaleString("en-IN")} was accepted! Proceed to payment in the THC app.`}] });
  };

  const handleRejectCounter = async (req, offer) => {
    if (!window.confirm("Reject this counter offer?")) return;
    const updatedReq = { ...req, offers: (req.offers||[]).map(o => o.id===offer.id ? {...o, status:"rejected"} : o) };
    try { await upsertOne(SK.requests, updatedReq); } catch { toast("Failed — please retry."); return; }
    saveRequests(requests.map(r => r.id===req.id ? updatedReq : r));
    toast("Counter rejected.");
    if (addNotif) addNotif(req.userId, { type:"offer-declined", title:`Offer declined — ${req.watch}`, body:"The seller has declined your counter offer.", link:{view:"request"}, emailTo:req.email, emailSubject:`Offer declined — ${req.watch}`, whatsappTo:req.phone, whatsappTemplate:"thc_buyer_update", whatsappParams:[{name:"1",value:req.name||"Collector"},{name:"2",value:req.watch},{name:"3",value:"The seller declined your counter. You can still negotiate or see other offers in the THC app."}] });
  };

  const withdrawOffer = async (req, offerId, reason) => {
    const offer = (req.offers||[]).find(o => o.id === offerId);
    if (!offer) return;
    const updatedReq = {
      ...req,
      offers: (req.offers||[]).map(o => o.id !== offerId ? o : {...o, status:"withdrawn", withdrawReason: reason}),
      updates: [...(req.updates||[]), {date:new Date().toISOString(), note:`${offer.sellerName} withdrew an offer. Reason: ${reason}`}],
    };
    try { await upsertOne(SK.requests, updatedReq); } catch { toast("Failed to withdraw offer — please retry."); return; }
    saveRequests(requests.map(r => r.id !== req.id ? r : updatedReq));
    setOfferModal(null);
    toast("Offer withdrawn. Buyer notified.");
    if (addNotif) addNotif(req.userId, {
      type:"offer",
      title:`Offer withdrawn — ${req.watch}`,
      body:`${offer.sellerName} withdrew an offer (₹${offer.price.toLocaleString("en-IN")}). Reason: ${reason}.`,
      link:{view:"request"},
      emailTo: req.email,
      emailSubject:`Offer withdrawn — ${req.watch}`,
      whatsappTo:req.phone,
      whatsappTemplate:"thc_buyer_update",
      whatsappParams:[{name:"1",value:req.name||"Collector"},{name:"2",value:req.watch},{name:"3",value:`A seller withdrew their ₹${offer.price.toLocaleString("en-IN")} offer. Reason: ${reason}.`}],
    });
  };

  const editOfferPrice = async (req, offerId, newPrice) => {
    const offer = (req.offers||[]).find(o => o.id === offerId);
    if (!offer) return;
    const updatedReq = {
      ...req,
      offers: (req.offers||[]).map(o => o.id !== offerId ? o : {...o, price: newPrice}),
      updates: [...(req.updates||[]), {date:new Date().toISOString(), note:`${offer.sellerName} reduced an offer from ₹${offer.price.toLocaleString("en-IN")} to ₹${newPrice.toLocaleString("en-IN")}.`}],
    };
    try { await upsertOne(SK.requests, updatedReq); } catch { toast("Failed to update price — please retry."); return; }
    saveRequests(requests.map(r => r.id !== req.id ? r : updatedReq));
    setOfferModal(null);
    toast("Price updated. Buyer notified.");
    if (addNotif) addNotif(req.userId, {
      type:"offer",
      title:`Price lowered on ${req.watch}`,
      body:`${offer.sellerName} reduced their offer to ₹${newPrice.toLocaleString("en-IN")}.`,
      link:{view:"request"},
      emailTo: req.email,
      emailSubject:`Price updated on ${req.watch}`,
      whatsappTo:req.phone,
      whatsappTemplate:"thc_buyer_update",
      whatsappParams:[{name:"1",value:req.name||"Collector"},{name:"2",value:req.watch},{name:"3",value:`${offer.sellerName} lowered their offer to ₹${newPrice.toLocaleString("en-IN")}. Open the THC app to review.`}],
    });
  };

  const sourcingCount = openSourcing.length + myClaims.length;

  const isAdmin = session?.user?.role==="admin"||(session?.user?.roles||[]).includes("admin");

  // Unverified sellers see a pending screen — no dashboard access
  if (!isAdmin && !mySeller?.verified) {
    const needsResubmit = mySeller?.resubmitRequested;
    return (
      <div className="fade-in" style={{maxWidth:520,margin:"64px auto",textAlign:"center",padding:"0 24px"}}>
        <div style={{fontSize:48,marginBottom:16}}>{needsResubmit?"✏️":"🕐"}</div>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:28,marginBottom:12}}>
          {needsResubmit?"Action Required":"Verification Pending"}
        </h2>
        {needsResubmit ? (
          <>
            <div style={{background:"#FFF8E7",border:"1.5px solid #F0D080",borderRadius:10,padding:"16px 20px",marginBottom:20,textAlign:"left"}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#7A5C00",letterSpacing:"0.1em",marginBottom:8}}>NOTE FROM ADMIN</div>
              <p style={{color:"#5C4400",lineHeight:1.7,margin:0,fontSize:13.5}}>{mySeller.resubmitNote||"Please resubmit your seller application with updated details."}</p>
            </div>
            <p style={{color:"var(--ink2)",lineHeight:1.8,marginBottom:24}}>
              Go to Marketplace → Become a Seller to update your details and resubmit. Your previous information has been pre-filled where possible.
            </p>
          </>
        ) : (
          <p style={{color:"var(--ink2)",lineHeight:1.8,marginBottom:24}}>
            Your seller account is under review. Our team verifies each seller before they can list watches or claim sourcing requests. You'll receive an email once you're approved.
          </p>
        )}
        {mySeller && !needsResubmit && (
          <div className="card" style={{textAlign:"left",padding:"20px 24px",marginBottom:24}}>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:12}}>Submitted Details</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 24px",fontSize:13.5}}>
              <div><span style={{color:"var(--ink3)"}}>Name</span><br/><strong>{mySeller?.name||session.user.name}</strong></div>
              <div><span style={{color:"var(--ink3)"}}>City</span><br/><strong>{mySeller?.city||"—"}</strong></div>
              <div><span style={{color:"var(--ink3)"}}>Handle</span><br/><strong>{mySeller?.handle?"@"+mySeller.handle:"—"}</strong></div>
              <div><span style={{color:"var(--ink3)"}}>ID Verified</span><br/><strong>{mySeller?.idVerified?"Uploaded ✓":"Not uploaded"}</strong></div>
            </div>
          </div>
        )}
        <div style={{color:"var(--ink3)",fontSize:13}}>Questions? Email us at <a href="mailto:hello@thc.watch" style={{color:"var(--accent)"}}>hello@thc.watch</a></div>
      </div>
    );
  }

  return (
    <>
    <div className="fade-in">
      <div style={{marginBottom:28}}>
        <span className="sl">Seller Dashboard</span>
        <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:34,letterSpacing:"-0.02em"}}>
            {mySeller?.name||session.user.name}
          </h2>
          {mySeller?.verified
            ? <span className="vbadge" style={mySeller.badge==="Top Seller"?{background:"#FFF0F5",borderColor:"#F4AACA",color:"#9B2C5C"}:{}}>✦ {mySeller.badge||"Trusted"}</span>
            : <span className="tag tag-red">Pending Verification</span>}
        </div>
      </div>

      <div className="mob-2col" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:28}}>
        {[["Total Listings",myListings.length],["Verified",myListings.filter(l=>l.verified).length],["Sales",mySeller?.sales||0],["Rating",mySeller?.rating?`★ ${mySeller.rating}`:"—"]].map(([k,v])=>(
          <div key={k} className="card" style={{textAlign:"center",padding:"20px 16px"}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,color:"#B8860B",marginBottom:4}}>{v}</div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#8A7E74",letterSpacing:"0.1em",textTransform:"uppercase"}}>{k}</div>
          </div>
        ))}
      </div>

      {mySeller && !mySeller.whatsapp && (()=>{
        const [waInput,setWaInput] = React.useState("");
        const [saving,setSaving]   = React.useState(false);
        return (
          <div style={{background:"#FFFBEB",border:"1.5px solid #F59E0B",borderRadius:10,padding:"14px 18px",marginBottom:20,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366" style={{flexShrink:0}}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.122.554 4.11 1.523 5.836L.057 23.215a.75.75 0 0 0 .927.908l5.42-1.427A11.955 11.955 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.853 0-3.587-.504-5.079-1.382l-.363-.214-3.762.99.989-3.668-.233-.373A9.955 9.955 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#92400E",marginBottom:6,letterSpacing:"0.05em"}}>ADD YOUR WHATSAPP / MOBILE NUMBER</div>
              <div style={{fontSize:12.5,color:"#78350F",marginBottom:10}}>Buyers can reach you directly on WhatsApp when they see your offers. Without it, the chat button won't appear.</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <input value={waInput} onChange={e=>setWaInput(e.target.value)} placeholder="+1 555 000 0000 or 9876543210" type="tel" inputMode="tel" autoComplete="tel" style={{flex:1,minWidth:160,fontSize:13,padding:"7px 12px",border:"1.5px solid #F59E0B",borderRadius:8,outline:"none",fontFamily:"'DM Mono',monospace"}}/>
                <button className="btn-ink" style={{fontSize:12,padding:"7px 18px",opacity:saving?0.6:1}} disabled={saving||!waInput.trim()} onClick={async()=>{
                  setSaving(true);
                  const { error } = await supabase.from("sellers").update({ whatsapp: waInput.trim() }).eq("id", mySeller.id);
                  if (error) { toast("Save failed — please retry."); setSaving(false); return; }
                  saveSellers(sellers.map(s=>s.id===mySeller.id?{...s,whatsapp:waInput.trim()}:s));
                  setSaving(false);
                  toast("WhatsApp number saved ✓");
                }}>{saving?"Saving…":"Save"}</button>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="thc-tabs" style={{marginBottom:24}}>
        {[
          ["overview","Overview"],
          ["listings","My Listings"],
          ["enquiries","Enquiries"+(myEnquiries.length>0?" ("+myEnquiries.length+")":"")],
          ["sourcing","Sourcing"+(sourcingCount>0?" ("+sourcingCount+")":"")],
          ["add","Add Listing"]
        ].map(([t,l])=>(
          <button key={t} className={`stab ${tab===t?"active":""}`} onClick={()=>setTab(t)}>{l.toUpperCase()}</button>
        ))}
      </div>

      {tab==="overview" && (
        <div>
          {/* ── Activity: needs your attention ── */}
          {(myEnquiries.filter(r=>r.status!=="Completed").length > 0 || myAccepted.length > 0 || openSourcing.length > 0) && (
            <div style={{marginBottom:24}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.12em",color:"var(--ink3)",marginBottom:10}}>NEEDS YOUR ATTENTION</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {myEnquiries.filter(r=>r.status!=="Completed").map(r=>(
                  <div key={r.id} style={{background:"rgba(5,150,105,.06)",border:"1.5px solid rgba(5,150,105,.2)",borderRadius:10,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,cursor:"pointer"}} onClick={()=>openDealRoom?openDealRoom("deal-enq-"+r.id):setTab("enquiries")}>
                    <div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--grn)",letterSpacing:"0.1em",marginBottom:2}}>PRICE OFFER</div>
                      <div style={{fontSize:14,fontWeight:600,color:"var(--ink)"}}>{r.watch}</div>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"var(--grn)"}}>₹{r.suggestedPrice?.toLocaleString("en-IN")}{r.listedPrice?<span style={{fontSize:11,color:"var(--ink3)",fontFamily:"'DM Mono',monospace"}}> vs ask ₹{r.listedPrice.toLocaleString("en-IN")}</span>:null}</div>
                    </div>
                    <button className="btn-ink" style={{fontSize:11,padding:"7px 14px",borderRadius:8,flexShrink:0}}>Reply →</button>
                  </div>
                ))}
                {myAccepted.map(r=>(
                  <div key={r.id} style={{background:"#FFFBEB",border:"1.5px solid #F0D080",borderRadius:10,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,cursor:"pointer"}} onClick={()=>setTab("sourcing")}>
                    <div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--gold)",letterSpacing:"0.1em",marginBottom:2}}>ACCEPTED — DISPATCH NEEDED</div>
                      <div style={{fontSize:14,fontWeight:600}}>{r.watch}</div>
                    </div>
                    <button className="btn-ghost" style={{fontSize:11,padding:"7px 14px",borderRadius:8,flexShrink:0}}>Dispatch →</button>
                  </div>
                ))}
                {openSourcing.length > 0 && (
                  <div style={{background:"var(--bg2)",border:"1px solid var(--bd)",borderRadius:10,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,cursor:"pointer"}} onClick={()=>setTab("sourcing")}>
                    <div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:2}}>SOURCING REQUESTS</div>
                      <div style={{fontSize:14,color:"var(--ink)"}}>{openSourcing.length} open — can you source any?</div>
                    </div>
                    <span style={{fontSize:11,color:"var(--ink3)"}}>View →</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {!mySeller?.verified && (
            <div style={{background:"var(--gbg)",border:"1.5px solid var(--gbd)",borderRadius:6,padding:20,marginBottom:20}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,marginBottom:8}}>Get Trusted ✦</div>
              <p style={{fontSize:13.5,color:"#4A423A",lineHeight:1.7}}>Complete 5 sales at 4.5+ average rating and you'll earn the Verified badge. It appears on all your listings and boosts buyer trust significantly.</p>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            {myListings.slice(0,4).map(l=>(
              <div key={l.id} className="card">
                {l.verified && <span className="tag tag-gold" style={{display:"inline-block",marginBottom:8}}>✦ Verified</span>}
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700}}>{l.brand} {l.model}</div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginBottom:8}}>{l.decade} · {l.condition}</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:"#B8860B"}}>₹{l.price?.toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
          {/* Reviews received */}
          {(() => {
            const myReviews = (reviews||[]).filter(r=>r.toSellerId===mySellerId && r.fromRole==="buyer");
            if (!myReviews.length) return null;
            return (
              <div style={{marginTop:24}}>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.12em",marginBottom:12}}>BUYER REVIEWS ({myReviews.length})</div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[...myReviews].reverse().slice(0,5).map(rv=>(
                    <div key={rv.id} style={{background:"var(--bg2)",borderRadius:8,padding:"12px 16px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                        <span style={{color:"#D97706",fontSize:14}}>{"★".repeat(rv.rating)}{"☆".repeat(5-rv.rating)}</span>
                        <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>{new Date(rv.createdAt).toLocaleDateString("en-IN")}</span>
                      </div>
                      {rv.review && <p style={{fontSize:13,color:"var(--ink2)",lineHeight:1.6,margin:0}}>{rv.review}</p>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {tab==="listings" && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:14}}>
          {myListings.length===0 && <div style={{fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74",padding:20}}>No listings yet.</div>}
          {myListings.map(l=>(
            <div key={l.id} className="card">
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                <div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700}}>{l.brand} {l.model}</div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74"}}>{l.decade} · Ref. {l.reference}</div>
                </div>
                <span className={`tag ${l.verified?"tag-gold":"tag-red"}`}>{l.verified?"✦ Live":"Pending"}</span>
              </div>
              <p style={{fontSize:12,color:"#4A423A",lineHeight:1.6,marginBottom:12}}>{l.description}</p>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:"#B8860B"}}>₹{l.price?.toLocaleString("en-IN")}</div>
                <button className="btn-ghost" style={{fontSize:11,padding:"6px 12px",color:"var(--red)",borderColor:"var(--rbd)"}} onClick={()=>{saveListings(listings.filter(x=>x.id!==l.id));toast("Removed.");}}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab==="enquiries" && (
        <div>
          {myEnquiries.length===0 && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No enquiries yet.</div>}
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {[...myEnquiries].reverse().map(r=>(
              <div key={r.id} className="card">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.1em",marginBottom:4}}>{r.id} · {new Date(r.createdAt).toLocaleDateString("en-IN")}</div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:18}}>{r.watch}</div>
                  </div>
                  <span className={`tag ${r.status==="Completed"?"tag-grn":"tag-gold"}`}>{r.status==="Completed"?"Closed":"New"}</span>
                </div>
                {r.suggestedPrice > 0 && (
                  <div style={{background:"rgba(5,150,105,.08)",border:"1px solid rgba(5,150,105,.2)",borderRadius:8,padding:"12px 16px",marginBottom:10}}>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--grn)",letterSpacing:"0.1em",marginBottom:4}}>PRICE OFFER</div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:"#111118"}}>₹{r.suggestedPrice.toLocaleString("en-IN")}</div>
                    {r.listedPrice && <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#8A7E74",marginTop:2}}>Your ask: ₹{r.listedPrice.toLocaleString("en-IN")}</div>}
                  </div>
                )}
                {r.message && (
                  <div style={{background:"var(--bg2)",borderRadius:8,padding:"12px 16px",marginBottom:10}}>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#8A7E74",marginBottom:4}}>MESSAGE FROM BUYER</div>
                    <p style={{fontSize:13,color:"#4A423A",lineHeight:1.65}}>{r.message}</p>
                  </div>
                )}
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#4A423A",marginBottom:10}}>
                  {r.name}
                </div>
                {r.status!=="Completed" && (
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <button className="btn-ink" style={{fontSize:12,padding:"8px 16px",flex:2,borderRadius:10}} onClick={()=>openDealRoom?openDealRoom("deal-enq-"+r.id):setView&&setView("chat")}>💬 Reply in Chat</button>
                    <button className="btn-ghost" style={{fontSize:11,padding:"8px 14px",color:"var(--grn)",borderColor:"var(--gnbd)",flex:1,borderRadius:10}} onClick={()=>{
                      saveRequests(requests.map(x=>x.id!==r.id?x:{...x,status:"Completed",updates:[...(x.updates||[]),{date:new Date().toISOString(),note:`Seller accepted offer of ₹${r.suggestedPrice||""}.`}]}));
                      if(openDealRoom) openDealRoom("deal-enq-"+r.id);
                    }}>✓ Accept</button>
                    <button className="btn-ghost" style={{fontSize:11,padding:"8px 14px",color:"var(--red)",borderColor:"rgba(225,29,72,.3)",flex:1,borderRadius:10}} onClick={()=>saveRequests(requests.map(x=>x.id!==r.id?x:{...x,status:"Completed",updates:[...(x.updates||[]),{date:new Date().toISOString(),note:"Seller declined the offer."}]}))}>✗ Decline</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab==="sourcing" && (
        <div>
          <div style={{marginBottom:20}}>
            <p style={{fontSize:13,color:"#4A423A",lineHeight:1.7}}>Buyers paid ₹99 to find these watches. Post one or more <strong>offers</strong> (price + condition + photos) per request. THC keeps ₹500 from each offer; the rest is yours. The buyer reviews all offers and picks.</p>
          </div>

          {(() => {
            // Single deduplicated list: open requests + requests where I have offers
            const reqsWithMyOffers = (requests||[]).filter(r =>
              (r.offers||[]).some(o => o.sellerId === mySellerId) &&
              !openSourcing.find(s => s.id === r.id)
            );
            const allRelevant = [...openSourcing, ...reqsWithMyOffers]
              .sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
            const isEmpty = allRelevant.length === 0 && myAccepted.length === 0 && myClaims.length === 0;
            if (isEmpty) return (
              <div style={{textAlign:"center",padding:48}}>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74",marginBottom:16}}>No open sourcing requests right now.</div>
                <button onClick={doRefresh} disabled={refreshing} className="btn-ghost" style={{fontSize:12}}>{refreshing?"Checking…":"↻ Check for new requests"}</button>
              </div>
            );
            return (
              <>
                <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}>
                  <button onClick={doRefresh} disabled={refreshing} style={{background:"none",border:"1px solid var(--bd)",borderRadius:20,padding:"4px 12px",fontSize:10,fontFamily:"'DM Mono',monospace",cursor:"pointer",color:"var(--ink3)"}}>
                    {refreshing?"…":"↻ Refresh"}
                  </button>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:28}}>
                  {allRelevant.filter(r => !myAccepted.some(a => a.id === r.id)).map(r => {
                    const myOffers = (r.offers||[]).filter(o => o.sellerId === mySellerId);
                    const openOffers = myOffers.filter(o => o.status==="open" && new Date(o.expiresAt) > new Date());
                    const isStillOpen = openSourcing.some(s => s.id === r.id);
                    return (
                      <div key={r.id} className="card">
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                          <div>
                            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.1em",marginBottom:4}}>
                              {r.id} · {new Date(r.createdAt).toLocaleDateString("en-IN")} · {r.type==="sourcing"?"SOURCING":"CATALOG"}
                            </div>
                            <div style={{fontFamily:"'Playfair Display',serif",fontSize:20}}>{r.watch}</div>
                          </div>
                          {openOffers.length > 0
                            ? <span className="tag tag-grn">Offered</span>
                            : <span className="tag tag-gold">Open</span>}
                        </div>
                        {(r.decade||r.condition||r.budget) && (
                          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
                            {r.decade && <span className="tag">{r.decade}</span>}
                            {r.condition && <span className="tag">{r.condition}</span>}
                            {r.budget && <span className="tag tag-grn">Budget ₹{parseInt(r.budget).toLocaleString("en-IN")}</span>}
                          </div>
                        )}
                        {(r.notes||r.message) && (
                          <div style={{background:"var(--bg2)",borderRadius:8,padding:"10px 14px",marginBottom:12}}>
                            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginBottom:3}}>BUYER NOTE</div>
                            <p style={{fontSize:13,color:"#4A423A",lineHeight:1.6}}>{r.notes||r.message}</p>
                          </div>
                        )}
                        {r.photos?.length > 0 && (
                          <div style={{display:"flex",gap:6,marginBottom:12,overflowX:"auto"}}>
                            {r.photos.map((p,i) => <img key={i} src={p} alt="" style={{height:60,width:60,objectFit:"cover",borderRadius:6,flexShrink:0}}/>)}
                          </div>
                        )}
                        {myOffers.map(o => {
                          const expired = o.status==="open" && new Date(o.expiresAt) <= new Date();
                          const s = expired ? "expired" : o.status;
                          const cs = getCounterState(o);
                          const buyerCountered = s==="open" && cs.pendingWith==="seller" && cs.count > 0;
                          const tagCls = s==="accepted" ? "tag-grn" : s==="open" ? "tag-gold" : "tag";
                          const statusLabel = s==="open" ? `Open · expires ${new Date(o.expiresAt).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}` : s==="accepted" ? "Accepted ✓" : s==="rejected" ? "Rejected" : s==="withdrawn" ? `Withdrawn — ${o.withdrawReason||""}` : "Expired";
                          return (
                            <div key={o.id} style={{background: buyerCountered ? "rgba(37,99,235,.05)" : "var(--bg2)", borderRadius:10, padding:"12px 14px", marginBottom:8, border: buyerCountered ? "1.5px solid rgba(37,99,235,.2)" : "1.5px solid transparent"}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                                <div>
                                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:2}}>{o.condition}</div>
                                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:18}}>₹{o.price.toLocaleString("en-IN")}</div>
                                </div>
                                <div style={{textAlign:"right"}}>
                                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)"}}>you receive</div>
                                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:12}}>₹{Math.max(0,o.price-OFFER_PLATFORM_FEE).toLocaleString("en-IN")}</div>
                                </div>
                              </div>
                              {buyerCountered && (
                                <div style={{background:"rgba(37,99,235,.09)",borderRadius:8,padding:"8px 12px",marginBottom:8}}>
                                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--blu)",letterSpacing:"0.1em",marginBottom:2}}>BUYER COUNTERED · ROUND {cs.count}</div>
                                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:18}}>₹{cs.currentPrice.toLocaleString("en-IN")}</div>
                                  {cs.last?.note && <div style={{fontSize:11,color:"var(--ink2)",marginTop:2}}>"{cs.last.note}"</div>}
                                </div>
                              )}
                              {cs.count > 1 && <CounterHistory offer={o}/>}
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:8,flexWrap:"wrap",gap:6}}>
                                <span className={`tag ${tagCls}`} style={{fontSize:9}}>{statusLabel}</span>
                                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                                  {buyerCountered && (
                                    <>
                                      <button className="btn-ink" style={{fontSize:10,padding:"5px 12px"}} onClick={()=>handleAcceptCounter(r, o)}>Accept ₹{cs.currentPrice.toLocaleString("en-IN")}</button>
                                      {cs.canCounter && <button className="btn-ghost" style={{fontSize:10,padding:"5px 10px"}} onClick={()=>setCounterModal({request:r, offer:o})}>Counter Again</button>}
                                      <button className="btn-ghost" style={{fontSize:10,padding:"5px 10px",color:"var(--red)"}} onClick={()=>handleRejectCounter(r, o)}>Reject</button>
                                    </>
                                  )}
                                  {s==="open" && !buyerCountered && (
                                    <>
                                      <button className="btn-ghost" style={{fontSize:10,padding:"5px 10px"}} onClick={()=>setOfferModal({mode:"edit", request:r, offer:o})}>Lower price</button>
                                      <button className="btn-ghost" style={{fontSize:10,padding:"5px 10px",color:"var(--red)"}} onClick={()=>setOfferModal({mode:"withdraw", request:r, offer:o})}>Withdraw</button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {isStillOpen && (
                          <button className="btn-gold" style={{width:"100%",padding:"11px 0",fontSize:13,marginTop: myOffers.length > 0 ? 8 : 0}} onClick={()=>setOfferModal({mode:"post", request:r})}>
                            {openOffers.length > 0 ? "Post another offer →" : "Make an offer →"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {/* My accepted deals — proceed to dispatch */}
          {myAccepted.length>0 && (
            <>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.12em",marginBottom:12,marginTop:24}}>YOUR DEALS — READY TO DISPATCH</div>
              <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:28}}>
                {[...myAccepted].reverse().map(r=>{
                  const myAccept = (r.offers||[]).find(o => o.sellerId === mySellerId && o.status === "accepted");
                  const canDispatch = !r.boxingVideoUrl;
                  const canRate = r.status==="Completed" && !r.sellerRated;
                  const statusC = {Dispatched:"#1D4ED8",Delivered:"#059669",Completed:"#059669",Disputed:"#DC2626","Match Found":"#2E7D52"}[r.status]||"#8A7E74";
                  return (
                  <div key={r.id} className="card">
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                      <div>
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.1em",marginBottom:4}}>{r.id} · accepted ₹{myAccept?.price.toLocaleString("en-IN")}</div>
                        <div style={{fontFamily:"'Playfair Display',serif",fontSize:18}}>{r.watch}</div>
                      </div>
                      <span className="tag" style={{background:statusC+"18",borderColor:statusC+"55",color:statusC}}>{r.status}</span>
                    </div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#4A423A",marginBottom:6}}>
                      Buyer: {r.name}
                    </div>
                    {myAccept?.shippingAddress && (
                      <div style={{background:"var(--bg2)",borderRadius:10,padding:"10px 14px",marginBottom:10}}>
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:4}}>SHIP TO</div>
                        <div style={{fontSize:13,color:"var(--ink2)",lineHeight:1.55}}>
                          <strong>{myAccept.shippingAddress.recipientName}</strong><br/>
                          {myAccept.shippingAddress.line1}{myAccept.shippingAddress.line2?", "+myAccept.shippingAddress.line2:""}<br/>
                          {myAccept.shippingAddress.city}, {myAccept.shippingAddress.state} {myAccept.shippingAddress.postalCode}
                        </div>
                      </div>
                    )}
                    {r.trackingNumber && (
                      <div style={{background:"rgba(5,150,105,.07)",borderRadius:10,padding:"8px 14px",marginBottom:10,fontFamily:"'DM Mono',monospace",fontSize:11}}>
                        📦 {r.courier || "Courier"} · <strong>{r.trackingNumber}</strong>
                      </div>
                    )}
                    {r.boxingVideoUrl && <div style={{marginBottom:10}}><video src={r.boxingVideoUrl} controls style={{width:"100%",borderRadius:8,maxHeight:160}}/></div>}
                    {canDispatch && r.status!=="Completed" && (
                      <div style={{background:"var(--bg2)",borderRadius:12,padding:"12px 14px",marginBottom:10}}>
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:8}}>DISPATCH DETAILS</div>
                        <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                          <input placeholder="Courier (e.g. Delhivery)" value={dispatchForm[r.id]?.courier||""} onChange={e=>setDispatchForm(f=>({...f,[r.id]:{...f[r.id],courier:e.target.value}}))} style={{flex:"1 1 120px",fontSize:12,padding:"8px 10px"}}/>
                          <input placeholder="Tracking number" value={dispatchForm[r.id]?.tracking||""} onChange={e=>setDispatchForm(f=>({...f,[r.id]:{...f[r.id],tracking:e.target.value}}))} style={{flex:"2 1 160px",fontSize:12,padding:"8px 10px"}}/>
                        </div>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          <button className="btn-ink" style={{fontSize:11,padding:"8px 16px"}} onClick={async()=>{
                            const tracking=dispatchForm[r.id]?.tracking?.trim()||"";
                            const courier=dispatchForm[r.id]?.courier?.trim()||"";
                            const note=`Watch dispatched by ${mySeller?.name||session.user.name}.${courier?` Courier: ${courier}.`:""}${tracking?` Tracking: ${tracking}.`:""}`;
                            const updated={...r,status:"Dispatched",trackingNumber:tracking,courier,updates:[...(r.updates||[]),{date:new Date().toISOString(),note}]};
                            try{await upsertOne(SK.requests,updated);}catch{toast("Save failed — please retry.");return;}
                            saveRequests(requests.map(x=>x.id!==r.id?x:updated));
                            if(addNotif) addNotif(r.userId,{type:"status",title:`Your ${r.watch} has been dispatched`,body:`${courier?courier+": ":""}${tracking||"Tracking details in deal room."}`,link:{view:"request"},emailTo:r.email,emailSubject:`${r.watch} dispatched`,whatsappTo:r.phone,whatsappTemplate:"thc_buyer_update",whatsappParams:[{name:"1",value:r.name||"Collector"},{name:"2",value:r.watch},{name:"3",value:`Your watch has been dispatched!${courier?` Courier: ${courier}.`:""}${tracking?` Tracking: ${tracking}.`:""}`}]});
                            toast("Dispatched! Buyer notified.");
                          }}>📦 Mark Dispatched</button>
                          <label className="btn-ghost" style={{fontSize:11,padding:"8px 14px",cursor:"pointer"}}>
                            🎥 Add Boxing Video
                            <input type="file" accept="video/*" style={{display:"none"}} onChange={async e=>{
                              const file=e.target.files[0]; if(!file) return;
                              toast("Uploading…");
                              const path=`videos/${r.id}/boxing.mp4`;
                              const{error}=await supabase.storage.from("watch-photos").upload(path,file,{upsert:true,contentType:file.type});
                              if(error){toast("Upload failed.");return;}
                              const{data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(path);
                              const updated={...r,boxingVideoUrl:publicUrl};
                              await upsertOne(SK.requests,updated);
                              saveRequests(requests.map(x=>x.id!==r.id?x:updated));
                              toast("Boxing video saved.");
                            }}/>
                          </label>
                        </div>
                      </div>
                    )}
                    {canRate && (
                      <button className="btn-ghost" style={{fontSize:11,padding:"8px 16px",color:"var(--gold)"}} onClick={()=>setRatingTarget({requestId:r.id,toUserId:r.userId,toName:r.name,fromRole:"seller"})}>
                        ★ Rate Buyer
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>
            </>
          )}

          {myClaims.length>0 && (
            <>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.12em",marginBottom:12}}>YOUR CLAIMS</div>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {[...myClaims].reverse().map(r=>{
                  const canDispatch = ["Match Found","Payment Pending"].includes(r.status);
                  const canRate = r.status==="Completed" && !r.sellerRated;
                  const statusC = {Dispatched:"#1D4ED8",Delivered:"#059669",Completed:"#059669",Disputed:"#DC2626"}[r.status]||"#8A7E74";
                  return (
                  <div key={r.id} className="card">
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                      <div>
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.1em",marginBottom:4}}>{r.id}</div>
                        <div style={{fontFamily:"'Playfair Display',serif",fontSize:18}}>{r.watch}</div>
                      </div>
                      <span className="tag" style={{background:statusC+"18",borderColor:statusC+"55",color:statusC}}>{r.status}</span>
                    </div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#4A423A",marginBottom:6}}>
                      Buyer: {r.name}
                    </div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginBottom:10}}>
                      Claimed {new Date(r.sellerClaim?.claimedAt).toLocaleDateString("en-IN")}
                    </div>
                    {r.escrowStatus==="held" && <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#B45309",background:"#FEF3C7",borderRadius:6,padding:"4px 10px",display:"inline-block",marginBottom:10}}>⏳ Escrow held</div>}
                    {r.boxingVideoUrl && <div style={{marginBottom:10}}><video src={r.boxingVideoUrl} controls style={{width:"100%",borderRadius:8,maxHeight:160}}/><div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginTop:3}}>Boxing video uploaded</div></div>}
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {canDispatch && (
                        <label className="btn-ink" style={{fontSize:11,padding:"7px 14px",cursor:"pointer",display:"inline-block"}}>
                          📦 Upload Boxing Video & Dispatch
                          <input type="file" accept="video/*" style={{display:"none"}} onChange={async e=>{
                            const file=e.target.files[0]; if(!file) return;
                            toast("Uploading boxing video…");
                            const path=`videos/${r.id}/boxing.mp4`;
                            const {error}=await supabase.storage.from("watch-photos").upload(path,file,{upsert:true,contentType:file.type});
                            if(error){toast("Upload failed.");return;}
                            const {data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(path);
                            const updated={...r,status:"Dispatched",boxingVideoUrl:publicUrl,updates:[...(r.updates||[]),{date:new Date().toISOString(),note:`Watch dispatched by ${mySeller?.name||session.user.name}. Boxing video recorded.`}]};
                            try{await upsertOne(SK.requests,updated);}catch{toast("Upload done but save failed — please retry.");return;}
                            saveRequests(requests.map(x=>x.id!==r.id?x:updated));
                            if(addNotif) addNotif(r.userId,{type:"status",title:`Your ${r.watch} has been dispatched`,body:"The seller has uploaded a boxing video and marked your order dispatched.",link:{view:"request"},emailTo:r.email,emailSubject:`${r.watch} dispatched — Track your order`,whatsappTo:r.phone,whatsappTemplate:"thc_buyer_update",whatsappParams:[{name:"1",value:r.name||"Collector"},{name:"2",value:r.watch},{name:"3",value:"Your watch has been dispatched! A boxing video has been recorded. Track your order in the THC app."}]});
                            toast("Dispatched! Buyer notified.");
                          }}/>
                        </label>
                      )}
                      {canRate && (
                        <button className="btn-ghost" style={{fontSize:11,padding:"7px 14px",color:"var(--gold)",borderColor:"var(--gold)"}} onClick={()=>setRatingTarget({requestId:r.id,toUserId:r.userId,toName:r.name,fromRole:"seller"})}>
                          ★ Rate Buyer
                        </button>
                      )}
                      {null /* direct buyer WhatsApp removed — all communication via platform chat */}
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {tab==="add" && (
        <div style={{maxWidth:540}}>
          <div className="mob-1col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            <div><label className="fl">Brand *</label><select value={lf.brand} onChange={e=>lu("brand",e.target.value)}><option value="">Select</option><option>HMT</option><option>Allwyn</option><option>Ricoh</option></select></div>
            <div><label className="fl">Model *</label><input value={lf.model} onChange={e=>lu("model",e.target.value)} placeholder="Pilot, Stride…" type="text" autoComplete="off" autoCapitalize="words" required/></div>
            <div><label className="fl">Reference</label><input value={lf.reference} onChange={e=>lu("reference",e.target.value)} placeholder="Ref no." type="text" autoComplete="off" autoCapitalize="characters" spellCheck={false}/></div>
            <div><label className="fl">Decade</label><select value={lf.decade} onChange={e=>lu("decade",e.target.value)}><option value="">Any</option>{["1960s","1970s","1980s","1990s"].map(d=><option key={d}>{d}</option>)}</select></div>
            <div><label className="fl">Condition</label><select value={lf.condition} onChange={e=>lu("condition",e.target.value)}><option value="">Select</option>{["Mint","Excellent","Good","Fair"].map(c=><option key={c}>{c}</option>)}</select></div>
            <div><label className="fl">Price ₹ *</label><input value={lf.price} onChange={e=>lu("price",e.target.value)} placeholder="2500" type="number" inputMode="numeric" min="0" autoComplete="off" required/></div>
            <div style={{gridColumn:"1/-1"}}><label className="fl">Description *</label><textarea value={lf.description} onChange={e=>lu("description",e.target.value)} rows={4} placeholder="Honest condition report. Dial, hands, case, movement." autoCapitalize="sentences" required/></div>
          </div>
          {/* Photo upload */}
          <div style={{marginBottom:18}}>
            <label className="fl">Watch photos</label>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:8}}>
              {listingPhotos.map((p,i)=>(
                <div key={i} style={{position:"relative"}}>
                  <img src={p.preview} alt="" style={{width:72,height:72,objectFit:"cover",borderRadius:8,border:"1px solid var(--bd)"}}/>
                  <button onClick={()=>setLP(prev=>prev.filter((_,j)=>j!==i))} style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:9,background:"var(--red)",color:"#fff",border:"none",cursor:"pointer",fontSize:10,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                </div>
              ))}
              {listingPhotos.length<6 && (
                <label style={{width:72,height:72,border:"2px dashed var(--bd)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:22,color:"var(--ink3)"}}>
                  +
                  <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>{
                    const files=Array.from(e.target.files).slice(0,6-listingPhotos.length);
                    setLP(prev=>[...prev,...files.map(f=>({file:f,preview:URL.createObjectURL(f)}))]);
                    e.target.value="";
                  }}/>
                </label>
              )}
            </div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>Up to 6 photos. First photo is the cover.</div>
          </div>
          <button className="btn-ink" onClick={submitListing} disabled={listingUpl} style={{opacity:listingUpl?0.6:1}}>{listingUpl?"Uploading…":"Submit listing →"}</button>
        </div>
      )}
    </div>
    {ratingTarget && <RatingModal target={ratingTarget} requests={requests} saveRequests={saveRequests} reviews={reviews} saveReviews={saveReviews} sellers={sellers} saveSellers={saveSellers} session={session} toast={toast} onClose={()=>setRatingTarget(null)}/>}
    {offerModal?.mode==="post" && (
      <MakeOfferModal request={offerModal.request} sellerId={mySellerId} sellerName={mySeller?.name||session.user.name} onClose={()=>setOfferModal(null)} onPost={(newOffer)=>postOffer(offerModal.request, newOffer)} toast={toast}/>
    )}
    {offerModal?.mode==="withdraw" && (
      <WithdrawOfferModal offer={offerModal.offer} onClose={()=>setOfferModal(null)} onConfirm={(reason)=>withdrawOffer(offerModal.request, offerModal.offer.id, reason)}/>
    )}
    {offerModal?.mode==="edit" && (
      <EditOfferPriceModal offer={offerModal.offer} onClose={()=>setOfferModal(null)} onConfirm={(newPrice)=>editOfferPrice(offerModal.request, offerModal.offer.id, newPrice)} toast={toast}/>
    )}
    {counterModal && <CounterOfferModal offer={counterModal.offer} by="seller" onSubmit={(price,note)=>handleSellerCounter(counterModal.request,counterModal.offer,price,note)} onClose={()=>setCounterModal(null)}/>}
    </>
  );
}

// ─── Rating Modal ─────────────────────────────────────────────────────────────
